import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button as MuiButton,
  Typography,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  AlertTitle,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  LinearProgress,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Breadcrumbs,
  Link,
  TableSortLabel,
  Tooltip,
  Divider,
  FormHelperText,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Add as AddIcon,
  Clear as ClearIcon,
  Search as SearchIcon,
  Analytics as AnalyticsIcon,
  TrendingUp as TrendingUpIcon,
  Timeline as TimelineIcon,
  Home as HomeIcon,
  FilterList as FilterListIcon,
  Sort as SortIcon,
  CloudDownload as CloudDownloadIcon,
  Storage as StorageIcon,
  ExpandMore as ExpandMoreIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  ArrowBack as ArrowBackIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { useApiClient } from '../hooks/useApiClient';

import UnifiedSamplingDialog from '../components/serp/UnifiedSamplingDialog';
import WorkflowStepper from '@/components/WorkflowStepper';
import { getWorkflowStatus } from '@/utils/moduleStatus';
import { clientService } from '../services/api/clientService';
import { useDataForSeoSnowflakeIntegration } from '../hooks/useDataForSeoSnowflakeIntegration';
import { useWorkflowContext } from '../context/WorkflowContext';
import { snowflakeService } from '../services/snowflakeService';

interface DataForSeoBatchResult {
  batch_id: string;
  total_keywords: number;
  processing_time_seconds: number;
  success_count: number;
  failed_count: number;
  blob_container: string;
  blob_prefix: string;
  download_urls: {
    results_file: string;
    summary_report: string;
  };
  summary_metrics: {
    total_search_volume: number;
    average_search_volume: number;
    zero_volume_keywords: number;
    high_volume_keywords: number;
    average_competition: number;
    average_cpc: number;
  };
  error_summary?: Record<string, {
    count: number;
    message: string;
    example_keywords: string[];
  }>;
}

interface SampledAnalysisResponse {
  success: boolean;
  batch_id: string;
  workflow_id: string;
  client_id: string;
  blob_path: string;
  download_urls: {
    results_file: string;
    summary_report: string;
  };
  processing_time_seconds: number;
  total_keywords: number;
  successful_keywords: number;
  failed_keywords: number;
  failed_keyword_details: Array<{
    keyword: string;
    error: string;
    error_code: number | string;
  }>;
  error_summary: Record<string, {
    count: number;
    message: string;
    example_keywords: string[];
  }>;
            summary_metrics: {
            total_search_volume: number;
            average_search_volume: number;
            zero_volume_keywords: number;
            high_volume_keywords: number;
            average_competition: number;
            average_cpc: number;
          };
  sampling_metadata: any;
}

interface VolumeSummary {
  metadata: {
    generated_at: string;
    total_keywords: number;
    successful_keywords: number;
    failed_keywords: number;
    location_code: number;
    language_code: string;
  };
  volume_statistics: {
    total_search_volume: number;
    average_search_volume: number;
    median_search_volume: number;
    high_volume_keywords: Array<{
      keyword: string;
      volume: number;
      competition: number;
      cpc: number;
    }>;
    low_volume_keywords: Array<{
      keyword: string;
      volume: number;
      competition: number;
      cpc: number;
    }>;
    zero_volume_keywords: string[];
  };
  competition_analysis: {
    average_competition: number;
    average_cpc: number;
    high_competition_keywords: Array<{
      keyword: string;
      competition: number;
      volume: number;
      cpc: number;
    }>;
    low_competition_keywords: Array<{
      keyword: string;
      competition: number;
      volume: number;
      cpc: number;
    }>;
  };
  trend_insights: {
    trending_up: Array<{
      keyword: string;
      volume: number;
      trend_change: number;
    }>;
    trending_down: Array<{
      keyword: string;
      volume: number;
      trend_change: number;
    }>;
    seasonal_keywords: Array<{
      keyword: string;
      volume: number;
      peak_months: number[];
    }>;
  };
  keyword_groups: Record<string, number>;
}

const LOCATION_OPTIONS = [
  { code: 2840, name: 'United States' },
  { code: 2124, name: 'Canada' },
  { code: 2826, name: 'United Kingdom' },
  { code: 2036, name: 'Australia' },
  { code: 2276, name: 'Germany' },
  { code: 2250, name: 'France' },
  { code: 2356, name: 'India' },
  { code: 2392, name: 'Japan' },
  { code: 2076, name: 'Brazil' },
  { code: 2484, name: 'Mexico' },
];

const DataForSeoTool: React.FC = () => {
  const { callApi } = useApiClient();
  const navigate = useNavigate();
  const { clientId, workflowId } = useParams<{ clientId?: string; workflowId?: string }>();
  
  // Determine if we're in workflow context
  const isWorkflowMode = !!(clientId && workflowId);
  
  // Get WorkflowContext for triggering stepper refresh and reading step statuses
  const { triggerRefresh, moduleStatuses } = useWorkflowContext();
  
  const [keywords, setKeywords] = useState<string[]>([]);
  const [keywordInput, setKeywordInput] = useState('');
  const [locationCode, setLocationCode] = useState(2840); // Default to US
  const [languageCode, setLanguageCode] = useState('en');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rateLimitMs, setRateLimitMs] = useState(100);
  const [batchSize, setBatchSize] = useState(100);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<DataForSeoBatchResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showCredentialsDialog, setShowCredentialsDialog] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [volumeSummary, setVolumeSummary] = useState<VolumeSummary | null>(null);
  const [showSummaryDialog, setShowSummaryDialog] = useState(false);
  const [detailedResults, setDetailedResults] = useState<any[]>([]); // State for detailed results
  const [sortField, setSortField] = useState<string>('searchVolume');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [filterText, setFilterText] = useState('');
  const [failedKeywords, setFailedKeywords] = useState<Array<{ keyword: string; error: string; error_code: number | string }>>([]);
  const [errorSummary, setErrorSummary] = useState<Record<string, { count: number; message: string; example_keywords: string[] }>>({});
  const [lastResultInfo, setLastResultInfo] = useState<any | null>(null);
  const [isLoadingLast, setIsLoadingLast] = useState(false);
  
  // Snowflake sampling states
  const [showSnowflakeSampling, setShowSnowflakeSampling] = useState(false);
  const [samplingStrategy, setSamplingStrategy] = useState('stratified');
  const [sampleSize, setSampleSize] = useState(100);
  const [loadingSnowflakeKeywords, setLoadingSnowflakeKeywords] = useState(false);
  const [isStepperCollapsed, setIsStepperCollapsed] = useState(false);
  const [clientData, setClientData] = useState<any>(null);
  const [autoLoadedKeywords, setAutoLoadedKeywords] = useState<{ count: number; source: string; timestamp?: string } | null>(null);
  const [loadingKeywords, setLoadingKeywords] = useState(false);
  
  // DataForSEO Snowflake integration
  const { 
    saveDataForSeoResultToSnowflake,
    isSaving: isSnowflakeSaving,
    saveError: snowflakeSaveError,
    saveSuccess: snowflakeSaveSuccess 
  } = useDataForSeoSnowflakeIntegration();
  
  // Track which batches have been saved to avoid duplicates
  const hasSavedToSnowflake = useRef<string | null>(null);
  
  // Removed eager latest-result fetch; we'll fetch lazily on button click using step status to show the button

  // Function to save results to Snowflake after detailed results are loaded
  const saveResultsToSnowflake = useCallback(async (detailedResults: any[], batchResult?: DataForSeoBatchResult) => {
    // Use provided batchResult or fall back to state
    const resultToUse = batchResult || result;
    
    console.log('saveResultsToSnowflake called with', detailedResults.length, 'results');
    console.log('Current result from state:', result);
    console.log('Provided batchResult:', batchResult);
    console.log('Using result:', resultToUse);
    console.log('hasSavedToSnowflake.current:', hasSavedToSnowflake.current);
    
    if (!resultToUse || hasSavedToSnowflake.current === resultToUse.batch_id) {
      console.log('Skipping save - either no result or already saved');
      return;
    }

    // Get the raw results from the blob if available
    let rawResults: any[] = [];
    
    try {
      // First try to get the raw results from blob storage
      if (resultToUse.download_urls?.results_file) {
        console.log('Attempting to fetch raw results from blob for Snowflake save...');
        
        // Extract blob path from the URL
        const url = new URL(resultToUse.download_urls.results_file);
        const pathSegments = url.pathname.split('/').filter(Boolean);
        const containerName = pathSegments[0];
        const blobPath = pathSegments.slice(1).join('/');
        
        // Use API to fetch content directly
        const workflowData = await callApi(`/api/v1/storage/get`, {
          method: 'GET',
          params: {
            path: blobPath,
            container: containerName
          }
        });
        
        if (workflowData && workflowData.results) {
          rawResults = workflowData.results;
          console.log('Successfully fetched raw results from blob:', rawResults.length);
        }
      }
    } catch (error) {
      console.warn('Failed to fetch raw results from blob, will reconstruct from detailed results:', error);
    }

    // If we couldn't get raw results from blob, reconstruct from detailed results
    if (rawResults.length === 0 && detailedResults.length > 0) {
      console.log('Converting detailed results back to raw format for Snowflake...');
      rawResults = detailedResults.map(r => ({
        success: true,
        keyword: r.keyword,
        data: {
          keyword: r.keyword,
          search_volume: r.searchVolume,
          competition: r.competition,
          competition_level: r.competitionLevel,
          cpc: r.cpc,
          avg_monthly_searches: r.avgMonthlySearches
        }
      }));
    }

    console.log('Calling saveDataForSeoResultToSnowflake with raw results...');
    console.log('Sample raw result:', rawResults[0]);
    
    try {
      await saveDataForSeoResultToSnowflake(
        resultToUse,
        keywords,
        locationCode,
        languageCode,
        workflowId,        // workflowId instead of undefined
        undefined,         // workflowName (we don't have this)
        clientId?.toString(),
        clientData?.name,
        rawResults
      );
      
      hasSavedToSnowflake.current = resultToUse.batch_id;
      console.log('✅ Snowflake save completed successfully for batch:', resultToUse.batch_id);
    } catch (error) {
      console.error('❌ Error calling saveDataForSeoResultToSnowflake:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        response: error.response
      });
      throw error; // Re-throw to maintain existing error handling
    }
  }, [result, keywords, locationCode, languageCode, clientId, clientData?.name, workflowId, saveDataForSeoResultToSnowflake, callApi]);

  // Auto-save to Snowflake when results are ready
  useEffect(() => {
    if (result && 
        result.download_urls.results_file && 
        !isSnowflakeSaving && 
        !snowflakeSaveSuccess &&
        hasSavedToSnowflake.current !== result.batch_id) {
      
      console.log('DataForSEO results ready, will save to Snowflake after loading detailed results...');
      
      // Persist keywords to localStorage for use in SERP Tool
      if (keywords.length > 0) {
        try {
          const keywordData = {
            keywords: keywords,
            timestamp: new Date().toISOString(),
            dataforseoBatchId: result.batch_id,
            locationCode: locationCode,
            languageCode: languageCode
          };
          
          if (clientId && workflowId) {
            // Store with workflow-specific key
            localStorage.setItem(`workflow_${clientId}_${workflowId}_dataforseo_keywords`, JSON.stringify(keywordData));
            console.log(`Persisted ${keywords.length} keywords for workflow ${clientId}/${workflowId}`);
          } else {
            // Store with general key for non-workflow usage
            localStorage.setItem('dataforseo_keywords_latest', JSON.stringify(keywordData));
            console.log(`Persisted ${keywords.length} keywords from latest DataForSeo analysis`);
          }
        } catch (error) {
          console.error('Failed to persist keywords to localStorage:', error);
        }
      }
    }
  }, [result, isSnowflakeSaving, snowflakeSaveSuccess, keywords, locationCode, languageCode, isWorkflowMode, clientId, workflowId]);

  // Parse numeric client ID for database operations
  const numericClientId = clientId && /^\d+$/.test(clientId) 
    ? parseInt(clientId, 10) 
    : undefined;

  // Handler for stepper collapse change
  const handleStepperCollapseChange = (collapsed: boolean) => {
    setIsStepperCollapsed(collapsed);
  };
  
  // Fetch client data
  useEffect(() => {
    const fetchClientData = async () => {
      if (numericClientId) {
        try {
          const client = await clientService.getClient(numericClientId);
          setClientData(client);
        } catch (error) {
          console.error('Failed to fetch client data:', error);
        }
      }
    };
    
    fetchClientData();
  }, [numericClientId]);

  // Set default date range (last 12 months)
  useEffect(() => {
    const today = new Date();
    const yearAgo = new Date(today);
    yearAgo.setFullYear(yearAgo.getFullYear() - 1);
    
    setDateTo(today.toISOString().split('T')[0]);
    setDateFrom(yearAgo.toISOString().split('T')[0]);
  }, []);

  // Auto-load keywords from SERP tool when in workflow context
  useEffect(() => {
    const loadPersistedKeywords = async () => {
      // Only show loading if we're in workflow mode and have no keywords yet
      if (isWorkflowMode && keywords.length === 0) {
        setLoadingKeywords(true);
      }
      
      try {
        let keywordData = null;
        
        // Add a small delay to show loading state even for localStorage access
        await new Promise(resolve => setTimeout(resolve, 200));
        
        // Try to load workflow-specific keywords from localStorage first
        if (clientId && workflowId) {
          const workflowKey = `workflow_${clientId}_${workflowId}_serp_keywords`;
          const storedData = localStorage.getItem(workflowKey);
          if (storedData) {
            keywordData = JSON.parse(storedData);
            console.log(`Loaded workflow-specific keywords from localStorage for ${clientId}/${workflowId}:`, keywordData);
          }
        }
        
        // Fallback to latest general SERP keywords from localStorage
        if (!keywordData) {
          const latestData = localStorage.getItem('serp_keywords_latest');
          if (latestData) {
            keywordData = JSON.parse(latestData);
            console.log('Loaded latest SERP keywords from localStorage:', keywordData);
          }
        }

        // If still no keywords found, try loading from Snowflake database
        if (!keywordData && clientId && workflowId) {
          console.log('🔍 No keywords found in localStorage, attempting to load from Snowflake...');
          
          try {
            // Add a minimum loading time to make the loading state visible
            const [snowflakeResponse] = await Promise.all([
              snowflakeService.getLatestSerpKeywords(clientId, workflowId),
              new Promise(resolve => setTimeout(resolve, 300)) // Minimum 300ms for Snowflake
            ]);
            
            if (snowflakeResponse.success && snowflakeResponse.data && snowflakeResponse.data.data) {
              const serpData = snowflakeResponse.data.data;
              
              // The API returns data in localStorage format: { cities, domains, keywords, serpBatchId, timestamp }
              if (serpData.keywords && serpData.keywords.length > 0) {
                keywordData = {
                  keywords: serpData.keywords,
                  cities: serpData.cities || [],
                  domains: serpData.domains || [],
                  timestamp: serpData.timestamp,
                  source: 'snowflake',
                  serpBatchId: serpData.serpBatchId
                };
                console.log(`Loaded ${serpData.keywords.length} keywords from Snowflake database`);
                
                // Cache the keywords in localStorage for future use
                try {
                  const cacheKey = `workflow_${clientId}_${workflowId}_serp_keywords`;
                  localStorage.setItem(cacheKey, JSON.stringify(keywordData));
                  console.log(`Cached Snowflake keywords to localStorage with key: ${cacheKey}`);
                } catch (cacheError) {
                  console.warn('Failed to cache Snowflake keywords in localStorage:', cacheError);
                }
              }
            } else {
              console.log('No keywords found in Snowflake:', snowflakeResponse.message);
            }
          } catch (snowflakeError) {
            console.warn('Failed to load keywords from Snowflake:', snowflakeError);
            // Continue gracefully - this is just a fallback
          }
        }
        
        // Load the keywords if found from any source
        if (keywordData && keywordData.keywords && keywordData.keywords.length > 0) {
          // Only auto-load if no keywords are already present
          if (keywords.length === 0) {
            setKeywords(keywordData.keywords);
            
            // Determine source for user feedback
            let sourceMessage = 'previous SERP analysis';
            if (keywordData.source === 'snowflake') {
              sourceMessage = 'Snowflake database';
            } else if (clientId && workflowId) {
              sourceMessage = 'workflow-specific SERP analysis';
            } else {
              sourceMessage = 'latest SERP analysis';
            }
            
            setSnackbarMessage(`Auto-loaded ${keywordData.keywords.length} keywords from ${sourceMessage}`);
            console.log(`Auto-loaded ${keywordData.keywords.length} keywords from ${sourceMessage}`);
            
            // Set auto-loaded state for UI feedback
            setAutoLoadedKeywords({
              count: keywordData.keywords.length,
              source: sourceMessage,
              timestamp: keywordData.timestamp
            });
          }
        } else {
          console.log('No keywords found in any source (localStorage or Snowflake)');
        }
      } catch (error) {
        console.error('Failed to load persisted keywords:', error);
      } finally {
        setLoadingKeywords(false);
      }
    };
    
    // Only run auto-load on component mount and when clientId/workflowId are available
    if (isWorkflowMode) {
      loadPersistedKeywords();
    }
  }, [clientId, workflowId, isWorkflowMode]); // Run when workflow context changes

  // Function to save to workflow storage
  const saveToWorkflowStorage = async (dataforSeoResult: DataForSeoBatchResult) => {
    if (!clientId || !workflowId) return;
    
    try {
      const response = await callApi('/api/v1/dataforseo/workflow-save', {
        method: 'POST',
        body: JSON.stringify({
          client_id: clientId,
          workflow_id: workflowId,
          batch_id: dataforSeoResult.batch_id,
          volume_results: dataforSeoResult,
          sampling_metadata: samplingStrategy ? {
            strategy: samplingStrategy,
            sample_size: sampleSize
          } : null
        })
      });
      
      if (response && response.success) {
        console.log('DataForSEO results saved to workflow storage:', response.blob_path);
      }
    } catch (error) {
      console.error('Failed to save DataForSEO results to workflow storage:', error);
    }
  };

  // Handler for Snowflake sampled keywords
  const handleSnowflakeKeywordsLoaded = (sampledKeywords: string[]) => {
    setKeywords(sampledKeywords);
    setShowSnowflakeSampling(false);
    setSnackbarMessage(`Loaded ${sampledKeywords.length} keywords from Snowflake sampling`);
    // Clear auto-loaded alert since user is manually loading keywords
    setAutoLoadedKeywords(null);
  };

  // Handler for manually loading SERP keywords
  const handleLoadSerpKeywords = async () => {
    setLoadingKeywords(true);
    
    try {
      let keywordData = null;
      
      // Add a small delay to show loading state even for localStorage access
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Try to load workflow-specific keywords from localStorage first
      if (clientId && workflowId) {
        const workflowKey = `workflow_${clientId}_${workflowId}_serp_keywords`;
        const storedData = localStorage.getItem(workflowKey);
        if (storedData) {
          keywordData = JSON.parse(storedData);
        }
      }
      
      // Fallback to latest SERP keywords from localStorage if no workflow-specific data found
      if (!keywordData) {
        const latestData = localStorage.getItem('serp_keywords_latest');
        if (latestData) {
          keywordData = JSON.parse(latestData);
        }
      }
      
      // If still no keywords found, try loading from Snowflake database
      if (!keywordData && clientId && workflowId) {
        console.log('🔍 [Manual] No keywords found in localStorage, attempting to load from Snowflake...');
        
        try {
          // Add a minimum loading time to make the loading state visible
          const [snowflakeResponse] = await Promise.all([
            snowflakeService.getLatestSerpKeywords(clientId, workflowId),
            new Promise(resolve => setTimeout(resolve, 300)) // Minimum 300ms for Snowflake
          ]);
          
          if (snowflakeResponse.success && snowflakeResponse.data && snowflakeResponse.data.data) {
            const serpData = snowflakeResponse.data.data;
            
            // The API returns data in localStorage format: { cities, domains, keywords, serpBatchId, timestamp }
            if (serpData.keywords && serpData.keywords.length > 0) {
              keywordData = {
                keywords: serpData.keywords,
                cities: serpData.cities || [],
                domains: serpData.domains || [],
                timestamp: serpData.timestamp,
                source: 'snowflake',
                serpBatchId: serpData.serpBatchId
              };
              console.log(`Loaded ${serpData.keywords.length} keywords from Snowflake database`);
              
              // Cache the keywords in localStorage for future use
              try {
                const cacheKey = `workflow_${clientId}_${workflowId}_serp_keywords`;
                localStorage.setItem(cacheKey, JSON.stringify(keywordData));
                console.log(`Cached Snowflake keywords to localStorage with key: ${cacheKey}`);
              } catch (cacheError) {
                console.warn('Failed to cache Snowflake keywords in localStorage:', cacheError);
              }
            }
          }
        } catch (snowflakeError) {
          console.warn('Failed to load keywords from Snowflake:', snowflakeError);
          // Continue gracefully - this is just a fallback
        }
      }
      
      // Load the keywords if found from any source
      if (keywordData && keywordData.keywords && keywordData.keywords.length > 0) {
        // Replace existing keywords with SERP keywords
        setKeywords(keywordData.keywords);
        
        // Determine source for user feedback
        let sourceMessage = 'SERP analysis';
        if (keywordData.source === 'snowflake') {
          sourceMessage = 'Snowflake database';
        } else if (clientId && workflowId) {
          sourceMessage = 'workflow-specific SERP analysis';
        } else {
          sourceMessage = 'latest SERP analysis';
        }
        
        setSnackbarMessage(
          `Loaded ${keywordData.keywords.length} keywords from ${sourceMessage}` +
          (keywordData.timestamp ? ` (${new Date(keywordData.timestamp).toLocaleString()})` : '')
        );
        
        // Set auto-loaded state for UI feedback
        setAutoLoadedKeywords({
          count: keywordData.keywords.length,
          source: sourceMessage,
          timestamp: keywordData.timestamp
        });
      } else {
        setSnackbarMessage('No SERP keywords found in localStorage or Snowflake database. Please run SERP analysis first.');
      }
    } catch (error) {
      console.error('Failed to load SERP keywords:', error);
      setSnackbarMessage('Failed to load SERP keywords');
    } finally {
      setLoadingKeywords(false);
    }
  };

  const handleAddKeywords = () => {
    const newKeywords = keywordInput
      .split('\n')
      .map(k => k.trim())
      .filter(k => k && !keywords.includes(k));
    
    if (newKeywords.length > 0) {
      setKeywords([...keywords, ...newKeywords]);
      setKeywordInput('');
      // Clear auto-loaded alert since user is manually modifying keywords
      setAutoLoadedKeywords(null);
    }
  };

  const handleRemoveKeyword = (index: number) => {
    setKeywords(keywords.filter((_, i) => i !== index));
    // Clear auto-loaded alert since user is manually modifying keywords
    setAutoLoadedKeywords(null);
  };

  const handleClearAll = () => {
    setKeywords([]);
    setKeywordInput('');
    setResult(null);
    setError(null);
    setVolumeSummary(null);
    setDetailedResults([]); // Clear detailed results
    setAutoLoadedKeywords(null); // Clear auto-loaded keywords alert
    setFailedKeywords([]); // Clear failed keywords
    setErrorSummary({}); // Clear error summary
  };

  const fetchSummaryReport = async (summaryUrl: string) => {
    try {
      // Extract blob path from the URL
      const url = new URL(summaryUrl);
      const pathSegments = url.pathname.split('/').filter(Boolean);
      const containerName = pathSegments[0]; // Get container name
      const blobPath = pathSegments.slice(1).join('/'); // Get blob path without container
      
      // Use API to fetch content directly (bypasses CORS)
      const response = await callApi(`/api/v1/storage/get`, {
        method: 'GET',
        params: {
          path: blobPath,
          container: containerName
        }
      });
      
      if (response) {
        setVolumeSummary(response);
        setShowSummaryDialog(true);
      }
    } catch (err) {
      console.error('Failed to fetch summary report:', err);
    }
  };

  const handleDownload = (url: string, filename: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleProcessBatch = async () => {
    if (keywords.length === 0) {
      setError('Please add at least one keyword.');
      return;
    }

    setLoading(true);
    setError(null);
    setProgress(0);
    setVolumeSummary(null);
    setDetailedResults([]); // Clear detailed results on new batch
    setFailedKeywords([]); // Clear failed keywords
    setErrorSummary({}); // Clear error summary

    try {
      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + 5, 90));
      }, 500);

      const requestBody = {
        keywords,
        location_code: locationCode,
        language_code: languageCode,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        username: username || undefined,
        password: password || undefined,
        rate_limit_ms: rateLimitMs,
        batch_size: batchSize,
      };

      const response = await callApi<DataForSeoBatchResult>('/api/v1/dataforseo/batch', {
        method: 'POST',
        body: JSON.stringify(requestBody),
      });

      clearInterval(progressInterval);
      setProgress(100);

      if (response) {
        setResult(response);
        
        // Set error information if available
        if (response.error_summary) {
          setErrorSummary(response.error_summary);
        }
        
        // Auto-fetch summary report
        if (response.download_urls.summary_report) {
          await fetchSummaryReport(response.download_urls.summary_report);
        }
        
        // Fetch detailed results directly from blob (CORS is fixed)
        if (response.download_urls.results_file) {
          try {
            console.log('Fetching detailed results from:', response.download_urls.results_file);
            
            // Fetch directly from the blob URL
            const blobResponse = await fetch(response.download_urls.results_file);
            
            if (blobResponse.ok) {
              const resultsData = await blobResponse.json();
              console.log('Fetched results data:', resultsData.length, 'items');
              
              if (resultsData && Array.isArray(resultsData)) {
                // Filter successful results and extract data
                const successfulResults = resultsData
                  .filter((r: any) => r.success && r.data)
                  .map((r: any) => ({
                    keyword: r.data.keyword,
                    searchVolume: r.data.search_volume || 0,
                    competition: r.data.competition || 0,
                    competitionLevel: r.data.competition_level || 'UNKNOWN',
                    cpc: r.data.cpc || 0,
                    avgMonthlySearches: r.data.avg_monthly_searches || r.data.search_volume || 0,
                  }));
                
                // Extract failed keywords
                const failures = resultsData
                  .filter((r: any) => !r.success)
                  .map((r: any) => ({
                    keyword: r.keyword,
                    error: r.error || 'Unknown error',
                    error_code: r.error_code || 'unknown'
                  }));
                
                if (failures.length > 0) {
                  setFailedKeywords(failures);
                  
                  // Build error summary
                  const summary: Record<string, any> = {};
                  failures.forEach(f => {
                    const code = String(f.error_code);
                    if (!summary[code]) {
                      summary[code] = {
                        count: 0,
                        message: f.error,
                        example_keywords: []
                      };
                    }
                    summary[code].count++;
                    if (summary[code].example_keywords.length < 3) {
                      summary[code].example_keywords.push(f.keyword);
                    }
                  });
                  setErrorSummary(summary);
                }
                
                console.log('Processed successful results:', successfulResults.length, 'items');
                console.log('Failed keywords:', failures.length, 'items');
                setDetailedResults(successfulResults);
                
                // Save to Snowflake after detailed results are loaded
                saveResultsToSnowflake(successfulResults);
              } else {
                console.error('Invalid results data format:', resultsData);
              }
            } else {
              console.error('Failed to fetch from blob:', blobResponse.status, blobResponse.statusText);
            }
          } catch (err) {
            console.error('Failed to fetch detailed results:', err);
            setSnackbarMessage('Unable to load detailed results. Summary data is still available.');
          }
        }
        
        // Save to workflow if in workflow mode
        if (isWorkflowMode && clientId && workflowId) {
          await saveToWorkflowStorage(response);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to process DataForSEO batch');
    } finally {
      setLoading(false);
      setProgress(0);
    }
  };

  const handleProcessSampledKeywords = async () => {
    if (!clientId || !workflowId) {
      setError('Client ID and Workflow ID are required for sampled analysis');
      return;
    }

    setLoading(true);
    setError(null);
    setProgress(0);
    setVolumeSummary(null);
    setDetailedResults([]); // Clear detailed results on new sampled analysis
    setFailedKeywords([]); // Clear failed keywords
    setErrorSummary({}); // Clear error summary

    try {
      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + 5, 90));
      }, 500);

      const requestBody = {
        client_id: clientId,
        workflow_id: workflowId,
        keywords: keywords, // Pass keywords directly (sampled by UI)
        location_code: locationCode,
        language_code: languageCode,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        username: username || undefined,
        password: password || undefined,
        rate_limit_ms: rateLimitMs,
        batch_size: batchSize,
        // Metadata about how keywords were sampled (for tracking purposes)
        sampling_metadata: {
          input_method: "ui_sampled",
          total_keywords_processed: keywords.length,
          sampling_source: autoLoadedKeywords?.source || "manual_input"
        }
      };

      const response = await callApi<SampledAnalysisResponse>('/api/v1/dataforseo/sampled-analysis', {
        method: 'POST',
        body: JSON.stringify(requestBody),
      });

      clearInterval(progressInterval);
      setProgress(100);
      
      // Trigger stepper refresh to show processing state immediately
      if (isWorkflowMode && triggerRefresh) {
        triggerRefresh();
        console.log('Triggered WorkflowStepper refresh after DataForSEO batch start');
      }

      if (response && response.success) {
        // Set failed keywords and error summary
        if (response.failed_keyword_details) {
          setFailedKeywords(response.failed_keyword_details);
        }
        if (response.error_summary) {
          setErrorSummary(response.error_summary);
        }
        // Use pre-signed download URLs from backend response (new pattern)
        const downloadUrls = response.download_urls || {
          // Fallback to old blob construction pattern if URLs not provided
          results_file: `/api/v1/blob/client-data/${response.blob_path}`,
          summary_report: `/api/v1/blob/client-data/${response.blob_path}`,
        };
        
        // Convert sampled response to match batch result format
        const batchResult: DataForSeoBatchResult = {
          batch_id: response.batch_id,
          total_keywords: response.total_keywords,
          processing_time_seconds: response.processing_time_seconds,
          success_count: response.successful_keywords,
          failed_count: response.total_keywords - response.successful_keywords,
          blob_container: 'client-data',
          blob_prefix: response.blob_path,
          download_urls: downloadUrls,  // Use pre-signed URLs
          summary_metrics: {
            ...response.summary_metrics,
            zero_volume_keywords: 0 // Default value since it's not provided in sampled response
          },
        };
        
        setResult(batchResult);
        
        // Fetch detailed results using pre-signed URL via proxy (to bypass CORS)
        try {
          console.log('Fetching detailed results from:', downloadUrls.results_file);
          
          // Extract blob path from the URL
          const url = new URL(downloadUrls.results_file);
          const pathSegments = url.pathname.split('/').filter(Boolean);
          const containerName = pathSegments[0]; // Get container name
          const blobPath = pathSegments.slice(1).join('/'); // Get blob path without container
          console.log('Container:', containerName, 'Blob path:', blobPath);
          
          // Use API to fetch content directly (bypasses CORS)
          const workflowData = await callApi(`/api/v1/storage/get`, {
            method: 'GET',
            params: {
              path: blobPath,
              container: containerName
            }
          });
          
          if (workflowData) {
            console.log('Workflow data keys:', Object.keys(workflowData));
            console.log('Workflow data sample:', workflowData);
            
            const results = workflowData.results || [];
            console.log('Raw results count:', results.length);
            console.log('Sample raw result:', results[0]);
            
            // Filter successful results and extract data
            const successfulResults = results
              .filter((r: any) => r.success && r.data)
              .map((r: any) => ({
                keyword: r.data.keyword,
                searchVolume: r.data.search_volume || 0,
                competition: r.data.competition || 0,
                competitionLevel: r.data.competition_level || 'UNKNOWN',
                cpc: r.data.cpc || 0,
                avgMonthlySearches: r.data.avg_monthly_searches || r.data.search_volume || 0,
              }));
            console.log('Successful results count:', successfulResults.length);
            console.log('Sample successful result:', successfulResults[0]);
            setDetailedResults(successfulResults);
            
            // Save to Snowflake after detailed results are loaded
            // Pass the batchResult directly since state update is async
            saveResultsToSnowflake(successfulResults, batchResult);
            
            // Set the volume summary from the workflow data
            if (workflowData.summary) {
              setVolumeSummary(workflowData.summary);
            }
          }
        } catch (err) {
          console.error('Failed to fetch detailed results from blob storage:', err);
        }
        
        setSnackbarMessage(`Processed ${response.total_keywords} keywords using UI sampling`);
        
        // Trigger stepper refresh to show completed state
        if (isWorkflowMode && triggerRefresh) {
          triggerRefresh();
          console.log('Triggered WorkflowStepper refresh after DataForSEO batch completion');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to process sampled keywords');
      
      // Trigger stepper refresh to show failed state
      if (isWorkflowMode && triggerRefresh) {
        triggerRefresh();
        console.log('Triggered WorkflowStepper refresh after DataForSEO batch failure');
      }
    } finally {
      setLoading(false);
      setProgress(0);
    }
  };

  // Sort and filter functions
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortedAndFilteredResults = () => {
    let filtered = detailedResults;
    
    // Apply filter
    if (filterText) {
      filtered = detailedResults.filter(result =>
        result.keyword.toLowerCase().includes(filterText.toLowerCase())
      );
    }
    
    // Apply sort
    return filtered.sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  return (
    <Box className="container mx-auto py-6">
      {/* Main Grid Container for Stepper + Content Layout */}
      {isWorkflowMode ? (
        <Grid 
          container 
          spacing={2} 
          columnGap={!isStepperCollapsed ? 4 : 0}
          direction="row"
          wrap="nowrap"
          sx={{ display: 'flex' }}
        >
          {/* Workflow Stepper Sidebar */}
          <Grid item sx={{ flex: '0 0 auto' }}>
            <WorkflowStepper 
              activeStep={6} // DataForSEO Analysis (new step after SERP)
              workflowId={workflowId || ''}
              clientId={clientId || ''}
              clientName={clientData?.name}
              onCollapseChange={handleStepperCollapseChange}
              moduleCompletionStatus={{
                module1: getWorkflowStatus(workflowId || '').module1,
                module2: getWorkflowStatus(workflowId || '').module2,
                module3: getWorkflowStatus(workflowId || '').module3,
                module4: getWorkflowStatus(workflowId || '').module4,
                serpAnalysis: false,
                dataForSeoAnalysis: false,
              }}
            />
          </Grid>
          
          {/* Main Content Area */}
          <Grid 
            item 
            sx={{
              flex: '1 1 auto',
              pl: isStepperCollapsed ? 1 : 3,
              overflow: 'auto'
            }}
          >
            {/* Content goes here */}
            <Box sx={{ flexGrow: 1 }}>
              {/* Breadcrumb navigation with back button */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Breadcrumbs 
                  aria-label="breadcrumb" 
                  sx={{ '& .MuiBreadcrumbs-separator': { color: 'text.secondary' } }}
                >
                  <Link 
                    color="inherit" 
                    href="/" 
                    onClick={(e) => { e.preventDefault(); navigate('/'); }}
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
                    Dashboard
                  </Link>
                  <Typography color="text.secondary">
                    {clientData?.name || `Client ${clientId}`} ({clientId} - {workflowId})
                  </Typography>
                  <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
                    <TrendingUpIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    Data For SEO Analysis
                  </Typography>
                </Breadcrumbs>
                
                <MuiButton
                  variant="outlined"
                  size="small"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate('/start-process')}
                  sx={{ ml: 2 }}
                >
                  Start Process
                </MuiButton>
              </Box>

                  {/* Auto-loaded Keywords Alert */}
                  {autoLoadedKeywords && (
                    <Alert 
                      severity="info" 
                      sx={{ mb: 3 }}
                      onClose={() => setAutoLoadedKeywords(null)}
                    >
                      <Typography variant="body2">
                        <strong>Auto-loaded {autoLoadedKeywords.count} keywords</strong> from {autoLoadedKeywords.source}
                        {autoLoadedKeywords.timestamp && (
                          <> • {new Date(autoLoadedKeywords.timestamp).toLocaleString()}</>
                        )}
                      </Typography>
                    </Alert>
                  )}

                  <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                    DataForSEO Search Volume Tool (Workflow Mode)
                  </Typography>

                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    Get comprehensive search volume data, competition metrics, and trend analysis for your keywords.
                    Results will be automatically saved to your workflow.
                  </Typography>

                  {/* Rest of the content will be rendered here */}
                  {renderMainContent()}
                </Box>
              </Grid>
            </Grid>
          ) : (
            /* Non-workflow mode - simple layout */
            <Box sx={{ flexGrow: 1 }}>
              {/* Breadcrumb Navigation */}
              <Breadcrumbs 
                aria-label="breadcrumb" 
                sx={{ mb: 3, '& .MuiBreadcrumbs-separator': { color: 'text.secondary' } }}
              >
                <Link 
                  color="inherit" 
                  href="/" 
                  onClick={(e) => { e.preventDefault(); navigate('/'); }}
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                  Dashboard
                </Link>
                <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
                  <TrendingUpIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                  Data For SEO Analysis
                </Typography>
              </Breadcrumbs>

              {/* Auto-loaded Keywords Alert */}
              {autoLoadedKeywords && (
                <Alert 
                  severity="info" 
                  sx={{ mb: 3 }}
                  onClose={() => setAutoLoadedKeywords(null)}
                >
                  <Typography variant="body2">
                    <strong>Auto-loaded {autoLoadedKeywords.count} keywords</strong> from {autoLoadedKeywords.source}
                    {autoLoadedKeywords.timestamp && (
                      <> • {new Date(autoLoadedKeywords.timestamp).toLocaleString()}</>
                    )}
                  </Typography>
                </Alert>
              )}

              <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                DataForSEO Search Volume Tool
              </Typography>

              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Get comprehensive search volume data, competition metrics, and trend analysis for your keywords.
              </Typography>

              {/* Rest of the content will be rendered here */}
              {renderMainContent()}
            </Box>
          )}
    </Box>
  );

  // Extract main content into a separate function for reuse
  function renderMainContent() {
    return (
      <>


        <Grid container spacing={3}>
          {/* Keywords Section */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Keywords ({keywords.length})
                </Typography>
                
                {/* Keyword Loading Options */}
                <Accordion sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1">
                      Keyword Loading Options
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      {/* Manual Input */}
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom>
                          <AddIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 1 }} />
                          Manual Input
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          variant="outlined"
                          placeholder="Enter keywords (one per line)"
                          value={keywordInput}
                          onChange={(e) => setKeywordInput(e.target.value)}
                          sx={{ mb: 1 }}
                        />
                        <MuiButton
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={handleAddKeywords}
                          disabled={!keywordInput.trim()}
                          fullWidth
                        >
                          Add Keywords
                        </MuiButton>
                      </Grid>
                      
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                      </Grid>
                      
                      {/* Load from SERP */}
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom>
                          <SearchIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 1 }} />
                          Load from SERP Analysis
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          Load keywords from a previously completed SERP analysis
                        </Typography>
                        <MuiButton
                          variant="outlined"
                          startIcon={loadingKeywords ? <CircularProgress size={16} /> : <SearchIcon />}
                          onClick={handleLoadSerpKeywords}
                          disabled={loadingKeywords}
                          fullWidth
                        >
                          {loadingKeywords ? 'Loading...' : 'Load from SERP'}
                        </MuiButton>
                      </Grid>
                      
                      {/* Load from Snowflake */}
                      {isWorkflowMode && (
                        <>
                          <Grid item xs={12}>
                            <Divider sx={{ my: 1 }} />
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="subtitle2" gutterBottom>
                              <StorageIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 1 }} />
                              Sample from Snowflake Database
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                              Use intelligent sampling strategies to load keywords from your Snowflake data
                            </Typography>
                            <MuiButton
                              variant="outlined"
                              onClick={() => setShowSnowflakeSampling(true)}
                              disabled={loadingSnowflakeKeywords}
                              startIcon={loadingSnowflakeKeywords ? <CircularProgress size={16} /> : <CloudDownloadIcon />}
                              fullWidth
                            >
                              {loadingSnowflakeKeywords ? 'Loading...' : 'Configure Snowflake Sampling'}
                            </MuiButton>
                          </Grid>
                        </>
                      )}
                      

                    </Grid>
                  </AccordionDetails>
                </Accordion>
                
                {/* Auto-loaded keywords alert */}
                {autoLoadedKeywords && (
                  <Alert 
                    severity="info" 
                    sx={{ mb: 2 }}
                    onClose={() => setAutoLoadedKeywords(null)}
                  >
                    Auto-loaded {autoLoadedKeywords.count} keywords from {autoLoadedKeywords.source}
                    {autoLoadedKeywords.timestamp && ` (${new Date(autoLoadedKeywords.timestamp).toLocaleString()})`}
                  </Alert>
                )}
                
                {/* Loading State for Keywords */}
                {loadingKeywords && (
                  <Box sx={{ 
                    mt: 2, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    p: 3,
                    border: '2px dashed #1976d2',
                    borderRadius: 2,
                    backgroundColor: 'rgba(25, 118, 210, 0.04)'
                  }}>
                    <CircularProgress size={24} sx={{ mr: 2, color: 'primary.main' }} />
                    <Typography variant="body2" color="primary.main" sx={{ fontWeight: 500 }}>
                      Loading keywords...
                    </Typography>
                  </Box>
                )}

                {/* Keywords List */}
                {keywords.length > 0 && !loadingKeywords && (
                  <Box sx={{ mt: 2, maxHeight: 300, overflow: 'auto' }}>
                    <List dense>
                      {keywords.map((keyword, index) => (
                        <ListItem key={index}>
                          <ListItemText primary={keyword} />
                          <ListItemSecondaryAction>
                            <IconButton edge="end" onClick={() => handleRemoveKeyword(index)}>
                              <DeleteIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
                
                {keywords.length === 0 && !loadingKeywords && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    No keywords added yet. Use one of the loading options above to add keywords.
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Settings Section */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Settings
                </Typography>
                
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Location</InputLabel>
                  <Select
                    value={locationCode}
                    onChange={(e) => setLocationCode(Number(e.target.value))}
                    label="Location"
                  >
                    {LOCATION_OPTIONS.map(loc => (
                      <MenuItem key={loc.code} value={loc.code}>
                        {loc.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      type="date"
                      label="From Date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      type="date"
                      label="To Date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Rate Limit (ms)"
                      type="number"
                      value={rateLimitMs}
                      onChange={(e) => setRateLimitMs(Number(e.target.value))}
                      inputProps={{ min: 50, max: 10000 }}
                      helperText="Milliseconds between API calls"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Batch Size</InputLabel>
                      <Select
                        value={batchSize}
                        label="Batch Size"
                        onChange={(e) => setBatchSize(Number(e.target.value))}
                      >
                        <MenuItem value={10}>10 keywords (Safest)</MenuItem>
                        <MenuItem value={25}>25 keywords (Conservative)</MenuItem>
                        <MenuItem value={50}>50 keywords (Balanced)</MenuItem>
                        <MenuItem value={100}>100 keywords (Recommended)</MenuItem>
                        <MenuItem value={200}>200 keywords (Aggressive)</MenuItem>
                        <MenuItem value={500}>500 keywords (Maximum)</MenuItem>
                      </Select>
                      <FormHelperText>
                        Smaller batches = lower failure rates but slower processing
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 2 }}>
                  <MuiButton
                    variant="outlined"
                    onClick={() => setShowCredentialsDialog(true)}
                    fullWidth
                  >
                    {username ? 'Credentials Set' : 'Set DataForSEO Credentials (Optional)'}
                  </MuiButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Action MuiButtons */}
        <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
          {isWorkflowMode && clientId && workflowId && (moduleStatuses?.step_statuses?.['7']?.completed) && (
            <MuiButton
              variant="outlined"
              color="secondary"
              size="large"
              startIcon={isLoadingLast ? <CircularProgress size={20} /> : <HistoryIcon />}
              onClick={async () => {
                try {
                  setIsLoadingLast(true);
                  // Lazily fetch latest result info if not already available
                  let info = lastResultInfo;
                  if (!info && workflowId) {
                    const latest = await snowflakeService.getLatestDataForSeoResultForWorkflow(workflowId);
                    if (latest.success && latest.data) {
                      info = latest.data;
                      setLastResultInfo(latest.data);
                    }
                  }

                  if (!info?.results_data) {
                    throw new Error('No latest results found for this workflow');
                  }

                  const resultsUrl = info.results_data.results_file || info.results_data.summary_report;
                  const url = new URL(resultsUrl);
                  const pathSegments = url.pathname.split('/').filter(Boolean);
                  const containerName = pathSegments[0];
                  const blobPath = pathSegments.slice(1).join('/');
                  const workflowData = await callApi(`/api/v1/storage/get`, {
                    method: 'GET',
                    params: { path: blobPath, container: containerName }
                  });

                  if (workflowData) {
                    const results = (workflowData.results || []).filter((r: any) => r.success && r.data).map((r: any) => ({
                      keyword: r.data.keyword,
                      searchVolume: r.data.search_volume || 0,
                      competition: r.data.competition || 0,
                      competitionLevel: r.data.competition_level || 'UNKNOWN',
                      cpc: r.data.cpc || 0,
                      avgMonthlySearches: r.data.avg_monthly_searches || r.data.search_volume || 0,
                    }));

                    setDetailedResults(results);
                    if (workflowData.summary) setVolumeSummary(workflowData.summary);

                    const batchResult: any = {
                      batch_id: info.batch_id || (workflowData.batch_id || ''),
                      total_keywords: info.results_data?.total_keywords || results.length,
                      processing_time_seconds: info.results_data?.processing_time_seconds || 0,
                      success_count: info.results_data?.success_count || results.length,
                      failed_count: info.results_data?.failed_count || 0,
                      blob_container: 'client-data',
                      blob_prefix: info.results_data?.blob_prefix || '',
                      download_urls: {
                        results_file: resultsUrl,
                        summary_report: info.results_data?.summary_report || resultsUrl
                      },
                      summary_metrics: info.results_data?.summary_metrics || {
                        total_search_volume: 0,
                        average_search_volume: 0,
                        zero_volume_keywords: 0,
                        high_volume_keywords: 0,
                        average_competition: 0,
                        average_cpc: 0
                      }
                    };
                    setResult(batchResult);
                    setSnackbarMessage('Loaded latest results');
                  }
                } catch (e: any) {
                  setError(e?.message || 'Failed to load latest results');
                } finally {
                  setIsLoadingLast(false);
                }
              }}
              disabled={loading || isLoadingLast}
              sx={{
                backgroundColor: 'success.dark',
                color: 'white',
                borderColor: 'success.main',
                '&:hover': {
                  backgroundColor: 'success.light',
                  borderColor: 'success.light',
                }
              }}
            >
              {isLoadingLast ? 'Loading Latest Results...' : 'Load Latest Results'}
            </MuiButton>
          )}
          {isWorkflowMode && clientId && workflowId ? (
            <MuiButton
              variant="contained"
              color="primary"
              size="large"
              startIcon={loading ? <CircularProgress size={20} /> : <TrendingUpIcon />}
              onClick={handleProcessSampledKeywords}
              disabled={loading}
              sx={{ flex: 1 }}
            >
              {loading ? 'Processing...' : 'Process Sampled Keywords'}
            </MuiButton>
          ) : (
            <MuiButton
              variant="contained"
              color="primary"
              size="large"
              startIcon={loading ? <CircularProgress size={20} /> : <TrendingUpIcon />}
              onClick={handleProcessBatch}
              disabled={loading || keywords.length === 0}
              sx={{ flex: 1 }}
            >
              {loading ? 'Processing...' : 'Get Search Volume Data'}
            </MuiButton>
          )}
          
          <MuiButton
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={handleClearAll}
            disabled={loading}
          >
            Clear All
          </MuiButton>
        </Box>

        {/* Progress Bar */}
        {loading && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={progress} />
          </Box>
        )}

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {/* Results Section */}
        {result && (
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Search Volume Results
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <MuiButton
                    variant="outlined"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownload(result.download_urls.results_file, `dataforseo_results_${result.batch_id}.json`)}
                  >
                    Download Data
                  </MuiButton>
                  {volumeSummary && (
                    <MuiButton
                      variant="contained"
                      size="small"
                      startIcon={<AnalyticsIcon />}
                      onClick={() => setShowSummaryDialog(true)}
                    >
                      View Analysis
                    </MuiButton>
                  )}
                </Box>
              </Box>
              
              {/* Summary Stats */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: 'primary.light', color: 'primary.contrastText' }}>
                    <Typography variant="h4">
                      {result.total_keywords}
                    </Typography>
                    <Typography variant="body2">
                      Total Keywords
                    </Typography>
                    {result.failed_count > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        ({result.success_count} successful)
                      </Typography>
                    )}
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: 'success.light', color: 'success.contrastText' }}>
                    <Typography variant="h4">
                      {result.summary_metrics.total_search_volume.toLocaleString()}
                    </Typography>
                    <Typography variant="body2">
                      Total Volume
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: 'info.light', color: 'info.contrastText' }}>
                    <Typography variant="h4">
                      {(result.summary_metrics.average_competition * 100).toFixed(0)}%
                    </Typography>
                    <Typography variant="body2">
                      Avg Competition
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: 'warning.light', color: 'warning.contrastText' }}>
                    <Typography variant="h4">
                      ${result.summary_metrics.average_cpc.toFixed(2)}
                    </Typography>
                    <Typography variant="body2">
                      Avg CPC
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Failed Keywords Alert - Less prominent */}
              {result.failed_count > 0 && (failedKeywords.length > 0 || Object.keys(errorSummary).length > 0) && (
                <Accordion sx={{ mb: 3, backgroundColor: 'warning.light' }}>
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ 
                      '& .MuiAccordionSummary-content': { 
                        alignItems: 'center' 
                      } 
                    }}
                  >
                    <WarningIcon sx={{ mr: 1, color: 'warning.dark' }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {result.failed_count} keywords could not be processed
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ backgroundColor: 'background.paper' }}>
                    {/* Error Summary */}
                    {Object.entries(errorSummary).map(([code, info]) => (
                      <Box key={code} sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {info.message} ({info.count} keywords)
                        </Typography>
                        {info.example_keywords.length > 0 && (
                          <Box sx={{ mt: 0.5, pl: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                              Examples: {info.example_keywords.map((kw, idx) => (
                                <span key={idx}>
                                  "{kw}"{idx < info.example_keywords.length - 1 ? ', ' : ''}
                                </span>
                              ))}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    ))}
                    
                    {/* Failed keywords table */}
                    {failedKeywords.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                          All Failed Keywords:
                        </Typography>
                        <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell>Keyword</TableCell>
                                <TableCell>Error</TableCell>
                                <TableCell align="center">Code</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {failedKeywords.map((item, idx) => (
                                <TableRow key={idx}>
                                  <TableCell>
                                    <Typography variant="caption">
                                      {item.keyword}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="caption">
                                      {item.error}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography variant="caption" color="text.secondary">
                                      {item.error_code}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Filter Box */}
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Filter keywords..."
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Box>

              {/* Results Table */}
              {detailedResults.length > 0 ? (
                <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <TableSortLabel
                            active={sortField === 'keyword'}
                            direction={sortField === 'keyword' ? sortDirection : 'asc'}
                            onClick={() => handleSort('keyword')}
                          >
                            Keyword
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="right">
                          <TableSortLabel
                            active={sortField === 'searchVolume'}
                            direction={sortField === 'searchVolume' ? sortDirection : 'asc'}
                            onClick={() => handleSort('searchVolume')}
                          >
                            Search Volume
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="center">
                          <TableSortLabel
                            active={sortField === 'competitionLevel'}
                            direction={sortField === 'competitionLevel' ? sortDirection : 'asc'}
                            onClick={() => handleSort('competitionLevel')}
                          >
                            Competition
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="right">
                          <TableSortLabel
                            active={sortField === 'competition'}
                            direction={sortField === 'competition' ? sortDirection : 'asc'}
                            onClick={() => handleSort('competition')}
                          >
                            Competition Score
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="right">
                          <TableSortLabel
                            active={sortField === 'cpc'}
                            direction={sortField === 'cpc' ? sortDirection : 'asc'}
                            onClick={() => handleSort('cpc')}
                          >
                            CPC
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="right">
                          <TableSortLabel
                            active={sortField === 'avgMonthlySearches'}
                            direction={sortField === 'avgMonthlySearches' ? sortDirection : 'asc'}
                            onClick={() => handleSort('avgMonthlySearches')}
                          >
                            Avg Monthly
                          </TableSortLabel>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getSortedAndFilteredResults().map((row, index) => (
                        <TableRow key={index} hover>
                          <TableCell component="th" scope="row">
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {row.keyword}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {row.searchVolume.toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={row.competitionLevel}
                              size="small"
                              color={
                                row.competitionLevel === 'HIGH' ? 'error' :
                                row.competitionLevel === 'MEDIUM' ? 'warning' :
                                row.competitionLevel === 'LOW' ? 'success' : 'default'
                              }
                              sx={{ minWidth: 80 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <LinearProgress
                              variant="determinate"
                              value={row.competition * 100}
                              sx={{ 
                                width: 60, 
                                height: 8, 
                                borderRadius: 4,
                                display: 'inline-flex',
                                backgroundColor: 'grey.300',
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor: 
                                    row.competition > 0.7 ? 'error.main' :
                                    row.competition > 0.3 ? 'warning.main' : 'success.main'
                                }
                              }}
                            />
                            <Typography variant="caption" sx={{ ml: 1 }}>
                              {(row.competition * 100).toFixed(0)}%
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              ${row.cpc.toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {row.avgMonthlySearches.toLocaleString()}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <CircularProgress />
                  <Typography variant="body2" sx={{ mt: 2 }}>
                    Loading detailed results...
                  </Typography>
                </Box>
              )}

              {/* Results Summary */}
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {getSortedAndFilteredResults().length} of {detailedResults.length} keywords
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Success rate: {((result.success_count / result.total_keywords) * 100).toFixed(1)}%
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Snackbar for notifications */}
        <Snackbar
          open={!!snackbarMessage}
          autoHideDuration={6000}
          onClose={() => setSnackbarMessage('')}
          message={snackbarMessage}
        />

        {/* Credentials Dialog */}
        <Dialog open={showCredentialsDialog} onClose={() => setShowCredentialsDialog(false)}>
          <DialogTitle>Set DataForSEO Credentials</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Username (Email)"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              margin="normal"
              helperText="Optional: Provide your DataForSEO username"
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              helperText="Optional: Provide your DataForSEO password"
            />
          </DialogContent>
          <DialogActions>
            <MuiButton onClick={() => setShowCredentialsDialog(false)}>Cancel</MuiButton>
            <MuiButton onClick={() => setShowCredentialsDialog(false)} variant="contained">
              Save
            </MuiButton>
          </DialogActions>
        </Dialog>

        {/* Unified Snowflake Sampling Dialog */}
        {isWorkflowMode && clientId && workflowId && (
          <UnifiedSamplingDialog
            open={showSnowflakeSampling}
            onClose={() => setShowSnowflakeSampling(false)}
            clientId={clientId}
            workflowId={workflowId}
            onKeywordsLoaded={handleSnowflakeKeywordsLoaded}
          />
        )}

        {/* Summary Dialog */}
        <Dialog 
          open={showSummaryDialog} 
          onClose={() => setShowSummaryDialog(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            Search Volume Analysis Summary
            <IconButton
              onClick={() => setShowSummaryDialog(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <ClearIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {volumeSummary && (
              <Grid container spacing={3}>
                {/* High Volume Keywords */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    <TrendingUpIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    High Volume Keywords
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Keyword</TableCell>
                          <TableCell align="right">Volume</TableCell>
                          <TableCell align="right">Competition</TableCell>
                          <TableCell align="right">CPC</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {volumeSummary.volume_statistics.high_volume_keywords.slice(0, 5).map((kw, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{kw.keyword}</TableCell>
                            <TableCell align="right">{kw.volume.toLocaleString()}</TableCell>
                            <TableCell align="right">{(kw.competition * 100).toFixed(0)}%</TableCell>
                            <TableCell align="right">${kw.cpc.toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>

                {/* Trending Keywords */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    <TimelineIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Trending Keywords
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Keyword</TableCell>
                          <TableCell align="right">Volume</TableCell>
                          <TableCell align="right">Trend</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {volumeSummary.trend_insights.trending_up.slice(0, 5).map((kw, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{kw.keyword}</TableCell>
                            <TableCell align="right">{kw.volume.toLocaleString()}</TableCell>
                            <TableCell align="right" sx={{ color: 'success.main' }}>
                              +{kw.trend_change.toFixed(1)}%
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>

                {/* Volume Distribution */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Volume Distribution
                  </Typography>
                  <Grid container spacing={2}>
                    {Object.entries(volumeSummary.keyword_groups).map(([range, count]) => (
                      <Grid item xs={6} md={2} key={range}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="subtitle2" color="text.secondary">
                            {range}
                          </Typography>
                          <Typography variant="h6">
                            {count}
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              </Grid>
            )}
          </DialogContent>
        </Dialog>


      </>
    );
  }
};

export default DataForSeoTool; 